import { useState } from "react";
import { Link } from "react-router-dom";
import { SOLUTIONS_MENU, RESOURCES_MENU } from "@/data/marketing";
import "./cinematic-exact.css";

// Installed from Fieldlink_Exact_Mockup_Website.zip: desktop renders the approved
// artwork itself with accessible hotspots; mobile is the supplied responsive build.
const IMG = "/cinematic";

const HOTSPOTS = [
  ["Fieldlink home", "/", { left: "4.3%", top: "1.5%", width: "12.1%", height: "6.2%" }],
  ["Pricing", "/pricing", { left: "33.2%", width: "4.36%", top: "2.54%", height: "3.5%" }],
  ["Log in", "/login", { left: "81.38%", width: "3.91%", top: "2.54%", height: "3.5%" }],
  ["Start free trial", "/register", { left: "86.33%", top: "1.95%", width: "10.03%", height: "4.3%" }],
  ["Start 7-day free trial", "/register", { left: "4.3%", top: "55.5%", width: "17%", height: "6%" }],
  ["Explore Fieldlink", "/features", { left: "22.5%", top: "55.5%", width: "14%", height: "6%" }],
  ["Manage Jobs", "/job-management-nz", { left: "4.8%", top: "90.5%", width: "10%", height: "4.7%" }],
  ["Track Everything", "/fleet-management-nz", { left: "36.5%", top: "90.5%", width: "11%", height: "4.7%" }],
  ["Stay Compliant", "/safety-management-nz", { left: "68%", top: "90.5%", width: "11%", height: "4.7%" }],
];

// Menu hotspots sit over the dropdown labels baked into the artwork.
const PRODUCT_MENU = [["The connected Job", "/#workflow"], ["Explore the product", "/#explorer"], ["Office + field", "/#why"], ["All features", "/features"]];
const MENUS = [
  ["Product", PRODUCT_MENU, { left: "19.21%", width: "5.92%", top: "2.54%", height: "3.5%" }],
  ["Solutions", SOLUTIONS_MENU, { left: "25.91%", width: "6.51%", top: "2.54%", height: "3.5%" }],
  ["Resources", RESOURCES_MENU, { left: "38.41%", width: "7.03%", top: "2.54%", height: "3.5%" }],
];

// On this page the homepage sections exist below, so "/#x" stays in-page.
const MenuLink = ({ to, children, ...rest }) =>
  to.startsWith("/#") ? <a href={to.slice(1)} {...rest}>{children}</a> : <Link to={to} {...rest}>{children}</Link>;

function HotspotMenu({ label, items, style }) {
  const [open, setOpen] = useState(false);
  const id = `fx-menu-${label.toLowerCase()}`;
  return (
    <div className="fx-menu" style={style}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
      onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.querySelector("button").focus(); } }}>
      <button type="button" className="fx-hotspot fx-menu-btn" aria-expanded={open} aria-controls={id}
        onClick={() => setOpen(!open)} data-testid={`fx-menu-${label.toLowerCase()}`}>
        <span className="fx-sr">{label}</span>
      </button>
      {open && (
        <div className="fx-menu-panel" id={id} data-testid={`fx-menu-panel-${label.toLowerCase()}`}>
          {items.map(([text, to]) => (
            <MenuLink key={text} to={to} onClick={() => setOpen(false)} data-testid={`fx-menu-item-${slug(text)}`}>{text}</MenuLink>
          ))}
        </div>
      )}
    </div>
  );
}

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function DesktopExact() {
  return (
    <section className="fx-desktop" aria-label="Fieldlink cinematic homepage visual" data-testid="fx-desktop">
      <img src={`${IMG}/01_FULL_DESIGN_REFERENCE.jpg`} width="1536" height="1024" fetchPriority="high"
        alt="Fieldlink cinematic construction homepage: orange excavator, floating job and safety dashboards, and product feature showcases. Dashboard figures are illustrative." />
      {MENUS.map(([label, items, style]) => <HotspotMenu key={label} label={label} items={items} style={style} />)}
      {HOTSPOTS.map(([label, to, style]) => (
        <Link key={label} className="fx-hotspot" to={to} style={style} aria-label={label} data-testid={`fx-hotspot-${slug(label)}`}>
          <span className="fx-sr">{label}</span>
        </Link>
      ))}
    </section>
  );
}

const TAGS = ["Jobs & Tenders", "Timesheets", "Crew & Plant", "Safety & Compliance", "Job Costing", "Invoicing"];
const ROWS = [["Subdivision Stage 1", "In progress"], ["Drainage Works", "In progress"], ["Roading Upgrade", "Scheduled"]];
const CARDS = [
  ["10_FEATURE_JOBS_REFERENCE.jpg", "Jobs from tender to completion", "/job-management-nz", "Manage jobs"],
  ["11_FEATURE_PLANT_REFERENCE.jpg", "Crew and plant in sync", "/fleet-management-nz", "Track crew and plant"],
  ["12_FEATURE_SAFETY_REFERENCE.jpg", "Safety made simple", "/safety-management-nz", "Safety management"],
];

function MobileHero() {
  return (
    <div className="fx-mobile" data-testid="fx-mobile">
      <header className="fx-m-header">
        <Link to="/" className="fx-brand"><span className="fx-mark" aria-hidden="true" />Fieldlink</Link>
        <div className="fx-m-header-right">
          <Link to="/login" className="fx-m-login" data-testid="fx-m-login">Log in</Link>
          <Link to="/register" className="fx-small-cta" data-testid="fx-m-header-trial">Start free trial ↗</Link>
        </div>
      </header>
      <section className="fx-m-hero" style={{ backgroundImage: `linear-gradient(180deg,rgba(7,10,14,.88) 0%,rgba(7,10,14,.68) 46%,rgba(7,10,14,.15) 72%,#080b0f 100%),url(${IMG}/02_HERO_LAYOUT_REFERENCE.jpg)` }}>
        <div className="fx-m-hero-content">
          <div className="fx-m-kicker">CIVIL CONSTRUCTION SOFTWARE</div>
          <p className="fx-m-title" aria-hidden="true">THE SITE.<br />THE CREW.<br />THE BUSINESS.<br /><em>CONNECTED.</em></p>
          <p className="fx-m-lead">Run your whole civil business from one place.</p>
          <div className="fx-m-tags">{TAGS.map((t) => <span key={t}>{t}</span>)}</div>
          <div className="fx-m-actions">
            <Link className="fx-primary" to="/register" data-testid="fx-m-trial">Start 7-day free trial ↗</Link>
            <Link className="fx-secondary" to="/features" data-testid="fx-m-explore">Explore Fieldlink ▶</Link>
          </div>
          <div className="fx-m-proof"><span>No credit card required</span><span>NZ based support</span><span>Built for civil</span></div>
        </div>
        <div className="fx-m-showcase">
          <div className="fx-m-showcase-title">Live Jobs <small>● 3 active</small></div>
          {ROWS.map(([n, s]) => <div className="fx-m-row" key={n}><span>{n}</span><strong>{s}</strong></div>)}
        </div>
      </section>
      <div className="fx-m-cards">
        {CARDS.map(([img, alt, to, label]) => (
          <div className="fx-m-card" key={img}>
            <img src={`${IMG}/${img}`} alt={alt} loading="lazy" decoding="async" />
            <Link to={to} data-testid={`fx-m-card-${slug(label)}`}><span className="fx-sr">{label}</span></Link>
          </div>
        ))}
      </div>
      <p className="fx-m-note">Dashboard figures shown are illustrative, not live data.</p>
    </div>
  );
}

export function CinematicExact() {
  return (
    <div className="fx-root">
        <h1 className="fx-sr">The site. The crew. The business. Connected. — Fieldlink civil construction software</h1>
      <DesktopExact />
      <MobileHero />
    </div>
  );
}
